import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable, tap, catchError } from 'rxjs';
import { PROJECT_CONTEXT_TOKEN } from '@cosmos/angular/utils';
import { environment } from '../../../../../../environments/environment';

export interface ConcretePour {
  _id: string;
  project: string;
  concrete: any;
  volume: number;
  date: Date;
  structuralElementType: string;
  elementSetId: string;
  linked: boolean;
  deliveryNote?: string;
  deliveryNoteId?: string;
  structuralElementRef?: string;
  controlLot?: string;
  linkMetadata?: {
    linkedVia: 'recommendation' | 'manual';
    confidence: 'high' | 'medium' | null;
    linkedAt: Date;
    recommendedLotIds: string[];
  };
}

export interface PourRecommendationResult {
  candidates: Array<{
    pour: ConcretePour;
    confidence: 'high' | 'medium';
    matchedFields: string[];
    warnings: Array<{
      field: string;
      strValue: string | null;
      pourValue: string | null;
      message: string;
    }>;
  }>;
  reason: string;
}

export interface LotRecommendation {
  canLinkToExisting: boolean;
  recommendedLot: any | null;
  reason: string;
}

@Injectable({
  providedIn: 'root'
})
export class PoursService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.API_URL}/pours`;

  // Signals for reactive state
  readonly pours = signal<ConcretePour[]>([]);
  readonly loadingPours = signal(false);
  readonly unlinkedPours = signal<ConcretePour[]>([]);
  readonly loadingUnlinkedPours = signal(false);

  find(query = ''): Observable<HttpResponse<any>> {
    return this.http.get<any>(`${this.apiUrl}${query}`, { observe: 'response' }).pipe(
      map(response => response)
    );
  }

  findWithCount(params: Record<string, any>): Observable<HttpResponse<any>> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    return this.http.get<any>(this.apiUrl, { params: httpParams, observe: 'response' });
  }

  countQuery(params: Record<string, any>): Observable<number> {
    let httpParams = new HttpParams().set('count', 'true');
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    return this.http.get<number>(this.apiUrl, { params: httpParams });
  }

  /**
   * Create a new pour
   */
  create(pourData: any, recommendation: boolean = false, projectId?: string): Observable<ConcretePour | { pour: ConcretePour, recommendation: LotRecommendation }> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post<ConcretePour | { pour: ConcretePour, recommendation: LotRecommendation }>(`${this.apiUrl}?recommendation=${recommendation}`, pourData, { context });
  }

  /**
   * Get all pours with optional filters
   */
  findAll(params?: {
    project?: string;
    concrete?: string;
    linked?: boolean;
    populate_concrete?: string;
    populate_project?: string;
    limit?: number;
    skip?: number;
    sort?: string;
    select?: string;
  }): Observable<ConcretePour[]> {
    this.loadingPours.set(true);
    
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ConcretePour[]>(this.apiUrl, { params: httpParams }).pipe(
      tap(pours => {
        this.pours.set(pours);
        this.loadingPours.set(false);
      })
    );
  }

  /**
   * Returns the total count of concrete pours matching the given filter params.
   * Calls the same endpoint as findAll with `count=true` appended.
   */
  count(params?: {
    project?: string;
    concrete?: string;
    linked?: boolean;
  }): Observable<number> {
    let httpParams = new HttpParams().set('count', 'true');
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<number>(this.apiUrl, { params: httpParams });
  }

  /**
   * Get pours for a specific project
   */
  findByProject(projectId: string): Observable<ConcretePour[]> {
    return this.findAll({ project: projectId });
  }

  /**
   * Get a specific pour by ID
   */
  findById(pourId: string, params?: {
    populate_concrete?: string;
    populate_project?: string;
    select?: string;
  }): Observable<ConcretePour> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ConcretePour>(`${this.apiUrl}/${pourId}`, { params: httpParams });
  }

  /**
   * Update a pour
   */
  update(pourId: string, updateData: Partial<ConcretePour>, projectId?: string): Observable<ConcretePour> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.patch<ConcretePour>(`${this.apiUrl}/${pourId}`, updateData, { context });
  }

  /**
   * Delete a pour
   */
  delete(pourId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${pourId}`);
  }

  /**
   * Get all unlinked pours for a project
   */
  getUnlinkedPours(projectId: string, withRecommendations = false): Observable<any[]> {
    this.loadingUnlinkedPours.set(true);
    const url = withRecommendations
      ? `${this.apiUrl}/unlinked/${projectId}?recommendations=true`
      : `${this.apiUrl}/unlinked/${projectId}`;
    return this.http.get<any[]>(url).pipe(
      tap(pours => {
        this.unlinkedPours.set(pours);
        this.loadingUnlinkedPours.set(false);
      })
    );
  }

  /**
   * Get lot recommendation for a specific pour
   */
  getLotRecommendation(pourId: string): Observable<LotRecommendation> {
    return this.http.get<LotRecommendation>(`${this.apiUrl}/${pourId}/recommendation`);
  }

  /**
   * Link a pour to a lot
   */
  linkPourToLot(pourId: string, createNewLot: boolean, existingLotId?: string, linkedVia: 'recommendation' | 'manual' = 'manual', projectId?: string): Observable<{ pour: ConcretePour, lot: any }> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post<{ pour: ConcretePour, lot: any }>(
      `${this.apiUrl}/${pourId}/link`,
      { pourId, createNewLot, existingLotId, linkedVia },
      { context }
    ).pipe(
      tap(() => {
        // Refresh unlinked pours after linking
        const currentPours = this.unlinkedPours();
        this.unlinkedPours.set(currentPours.filter(p => p._id !== pourId));
      })
    );
  }

  /**
   * Part 8: Get pour recommendations for linking to an STR (Pour-first model).
   * Returns ranked candidates with confidence scores.
   * Calls GET /pours/recommendations?strId=<strId>
   */
  getPourRecommendationsForStr(strId: string): Observable<PourRecommendationResult> {
    return this.http.get<PourRecommendationResult>(`${this.apiUrl}/recommendations`, {
      params: { strId }
    });
  }

  getPourRecommendationsBatch(strIds: string[]): Observable<Record<string, PourRecommendationResult>> {
    return this.http.post<Record<string, PourRecommendationResult>>(
      `${this.apiUrl}/recommendations/batch`,
      { strIds }
    );
  }

  /**
   * Resolve the concrete field on a pour (from PJ pipeline raw string) to a ConcreteMixture ObjectId.
   */
  resolveConcrete(pourId: string, concreteId: string, projectId?: string): Observable<ConcretePour> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post<ConcretePour>(`${this.apiUrl}/${pourId}/resolve-concrete`, { concreteId }, { context } );
  }

  attachFile(pourId: string, file: File, projectId?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http.post(`${this.apiUrl}/${pourId}/files`, formData, { observe: 'response', context });
  }

  servePourFileBlob(pourId: string, fileId: string): Observable<{ url: string; contentType: string }> {
    return this.http.get(
      `${this.apiUrl}/${pourId}/files/${fileId}/serve`,
      { responseType: 'blob', withCredentials: true }
    ).pipe(
      map((blob: Blob) => ({ url: URL.createObjectURL(blob), contentType: blob.type }))
    );
  }
}
